import { TimeReportResolution, TimeReportStatusV2 } from "model/TimeReport";
import type { TFunction } from "i18next";
import type {
  TimeReportsV2Group,
  TimeReportsV2Item,
} from "services/api/company/timeReports.service";
import {
  formatDateTimeShortLabel,
  formatDayMonthLabel,
  formatMinutesDuration,
  minutesBetweenIsoStrict,
  formatSignedMinutesDuration,
  formatTimeRangeLabel,
  formatWeekdayLabel,
  parseSignedDurationToMinutes,
  signedMinutesBetweenIso,
} from "web-apps/company/utils/timeAndDate";
import {
  getTimeReportAbsenceResolutionLabel,
  isTimeReportAbsenceResolution,
} from "web-apps/company/utils/absenceLabels";
import type {
  PersonDetailRowViewModel,
  PersonDetailSelection,
  PersonDetailTag,
} from "../../models/personView.types";
import {
  getPlannedMinutes,
  getReportedMinutes,
} from "../../models/personView.timeReportsCalculations";
import { resolveSignedMinutesForResolution } from "../../models/personView.actions";
import { resolveStampedTimeRangeValue } from "../../models/personView.stampedTime.mapper";

interface DeviationTagData {
  type: "overtime" | "undertime" | "startDeviation" | "endDeviation";
  displayMinutes: number;
}

interface WorkedMinutesData {
  workedMinutes: number;
  plannedMinutes: number;
  totalMinutes: number;
}

const isUnreportedItem = (item: TimeReportsV2Item): boolean => {
  return (
    (item.status === TimeReportStatusV2.UNDEFINED ||
      item.status === TimeReportStatusV2.NEW) &&
    item.resolution === TimeReportResolution.UNDEFINED
  );
};

export interface PersonRowSummaryViewModel {
  shifts: number;
  plannedTime: string;
  approvedTime: string;
  toApproveTime: string;
  unreportedTime: string;
  absenceTime: string;
  deviationTime: string;
}

const formatDetailsDuration = (
  value: string | null,
  signed: boolean,
): string => {
  const minutes = parseSignedDurationToMinutes(value);
  if (minutes == null || minutes === 0) return "0h";
  if (signed) return formatSignedMinutesDuration(minutes);
  return formatMinutesDuration(Math.abs(minutes));
};

const formatAbsenceDuration = (value: string | null): string => {
  const minutes = parseSignedDurationToMinutes(value);
  if (minutes == null || minutes === 0) return "0h";
  return formatSignedMinutesDuration(-Math.abs(minutes));
};

const resolveRange = (
  startValue: string | null,
  endValue: string | null,
): { start: string; end: string } | null => {
  if (!startValue || !endValue) return null;
  return { start: startValue, end: endValue };
};

const areRangesEqual = (
  left: { start: string; end: string } | null,
  right: { start: string; end: string } | null,
): boolean => {
  if (!left && !right) return true;
  if (!left || !right) return false;
  return left.start === right.start && left.end === right.end;
};

/** Check whether reported and stamped times differ more than the edited badge threshold. */
const hasReportedVsStampedDifferenceOverThreshold = (
  item: TimeReportsV2Item,
  thresholdMinutes: number,
): boolean => {
  const hasReportedStampedTimeDifference =
    Boolean(
      item.reportedStartTime &&
      item.reportedEndTime &&
      item.stampedStartTime &&
      item.stampedEndTime,
    ) &&
    (() => {
      const startDiff = Math.abs(
        signedMinutesBetweenIso(
          item.reportedStartTime,
          item.stampedStartTime,
        ) ?? 0,
      );
      const endDiff = Math.abs(
        signedMinutesBetweenIso(item.reportedEndTime, item.stampedEndTime) ?? 0,
      );
      const reportedDuration = minutesBetweenIsoStrict(
        item.reportedStartTime,
        item.reportedEndTime,
      );
      const stampedDuration = minutesBetweenIsoStrict(
        item.stampedStartTime,
        item.stampedEndTime,
      );
      if (reportedDuration == null || stampedDuration == null) return false;
      const durationDiff = Math.abs(reportedDuration - stampedDuration);

      return (
        startDiff > thresholdMinutes ||
        endDiff > thresholdMinutes ||
        durationDiff > thresholdMinutes
      );
    })();
  return hasReportedStampedTimeDifference;
};

/** Clamp small minute values to zero based on a threshold tolerance. */
const normalizeWithThreshold = (
  value: number,
  thresholdMinutes: number,
): number => {
  const safeThreshold = Math.max(0, thresholdMinutes);
  return Math.abs(value) <= safeThreshold ? 0 : value;
};

/**
 * Resolve a deviation tag from reported/planned minutes and optional flex-time rules.
 * Returns null when no meaningful deviation should be shown.
 */
const resolveDeviationTagData = (
  item: TimeReportsV2Item,
  thresholdMinutes: number,
  includeFlexTimeDeviations: boolean,
): DeviationTagData | null => {
  if (!includeFlexTimeDeviations) return null;

  const reportedMinutes = getReportedMinutes(item);
  if (reportedMinutes == null) return null;

  const netDeviation = normalizeWithThreshold(
    reportedMinutes - (getPlannedMinutes(item) ?? 0),
    thresholdMinutes,
  );

  if (netDeviation !== 0) {
    return {
      type: netDeviation > 0 ? "overtime" : "undertime",
      displayMinutes: netDeviation,
    };
  }

  const startDiffRaw = signedMinutesBetweenIso(
    item.reportedStartTime,
    item.plannedStartTime,
  );
  if (startDiffRaw != null) {
    const startDiff = normalizeWithThreshold(startDiffRaw, thresholdMinutes);
    if (startDiff !== 0) {
      return { type: "startDeviation", displayMinutes: startDiff };
    }
  }

  const endDiffRaw = signedMinutesBetweenIso(
    item.reportedEndTime,
    item.plannedEndTime,
  );
  if (endDiffRaw != null) {
    const endDiff = normalizeWithThreshold(endDiffRaw, thresholdMinutes);
    if (endDiff !== 0) {
      return { type: "endDeviation", displayMinutes: endDiff };
    }
  }

  return null;
};

/**
 * Build the list of time lines shown in a detail row.
 * Keeps all relevant sources (reported/planned/stamped) to avoid hiding context.
 */
const resolveTimeRows = (
  item: TimeReportsV2Item,
  locale: string,
  translate: TFunction,
): string[] => {
  const isUnreportedState = isUnreportedItem(item);
  const reportedRange = resolveRange(
    item.reportedStartTime,
    item.reportedEndTime,
  );
  const plannedRange = resolveRange(item.plannedStartTime, item.plannedEndTime);
  const stampedRange = resolveRange(item.stampedStartTime, item.stampedEndTime);
  const stampedLine = resolveStampedLine(
    item.stampedStartTime,
    item.stampedEndTime,
    locale,
    translate,
  );

  if (isUnreportedState) {
    return resolveUnreportedTimeRows(plannedRange, locale, translate);
  }
  if (!reportedRange) {
    return resolveRowsWithoutReported(plannedRange, stampedLine, locale, translate);
  }
  return resolveRowsWithReported(
    reportedRange,
    plannedRange,
    stampedRange,
    stampedLine,
    item.resolution,
    locale,
    translate,
  );
};

const resolveUnreportedTimeRows = (
  plannedRange: { start: string; end: string } | null,
  locale: string,
  translate: TFunction,
): string[] => {
  if (!plannedRange) return ["--"];
  return [
    `${translate("TimeReports.Planned")} ${formatTimeRangeLabel(plannedRange.start, plannedRange.end, locale)}`,
  ];
};

const resolveRowsWithoutReported = (
  plannedRange: { start: string; end: string } | null,
  stampedLine: string | null,
  locale: string,
  translate: TFunction,
): string[] => {
  if (stampedLine) return [stampedLine];
  if (!plannedRange) return ["--"];
  return [
    `${translate("TimeReports.Planned")} ${formatTimeRangeLabel(plannedRange.start, plannedRange.end, locale)}`,
  ];
};

const resolveRowsWithReported = (
  reportedRange: { start: string; end: string },
  plannedRange: { start: string; end: string } | null,
  stampedRange: { start: string; end: string } | null,
  stampedLine: string | null,
  resolution: TimeReportResolution | null,
  locale: string,
  translate: TFunction,
): string[] => {
  const differsFromPlanned = plannedRange
    ? !areRangesEqual(reportedRange, plannedRange)
    : false;
  const differsFromStamped = stampedLine
    ? stampedRange
      ? !areRangesEqual(reportedRange, stampedRange)
      : true
    : false;
  const reportedLabel =
    resolution === TimeReportResolution.OVERDUE
      ? translate("CompanyTimeReport.Overdue")
      : translate("TimeReports.Reported");
  const reportedLine = `${reportedLabel} ${formatTimeRangeLabel(reportedRange.start, reportedRange.end, locale)}`;

  if (!differsFromPlanned && !differsFromStamped) return [reportedLine];
  if (differsFromStamped && stampedLine) return [reportedLine, stampedLine];
  if (!differsFromPlanned || !plannedRange) return [reportedLine];
  return [
    reportedLine,
    `${translate("TimeReports.Planned")} ${formatTimeRangeLabel(plannedRange.start, plannedRange.end, locale)}`,
  ];
};

const resolveStampedLine = (
  stampedStartTime: string | null,
  stampedEndTime: string | null,
  locale: string,
  translate: TFunction,
): string | null => {
  const stampedTimeRange = resolveStampedTimeRangeValue(
    stampedStartTime,
    stampedEndTime,
    locale,
  );
  if (stampedTimeRange == null) return null;
  return `${translate("TimeReports.Stamped")} ${stampedTimeRange}`;
};

const resolveDeviationTag = (
  deviation: DeviationTagData | null,
  translate: TFunction,
): PersonDetailTag | null => {
  if (!deviation) return null;
  if (deviation.type === "overtime" || deviation.type === "undertime") {
    return {
      text: formatSignedMinutesDuration(deviation.displayMinutes),
      iconName: "v2/warning-triangle",
      tone: "danger",
    };
  }
  return {
    text: `${translate(
      deviation.type === "startDeviation"
        ? "TimeReports.StartTimeDeviation"
        : "TimeReports.EndTimeDeviation",
    )} ${formatSignedMinutesDuration(deviation.displayMinutes)}`,
    iconName: "v2/warning-triangle",
    tone: "danger",
  };
};

const resolveStatusTag = (
  item: TimeReportsV2Item,
  isEdited: boolean,
  translate: TFunction,
): PersonDetailTag => {
  if (isApprovedStatus(item)) return resolveApprovedStatusTag(translate);
  if (isEdited) return resolveEditedStatusTag(translate);
  if (isTimeReportAbsenceResolution(item.resolution)) {
    return resolveAbsenceStatusTag(item, translate);
  }
  if (item.status === TimeReportStatusV2.REJECTED) return resolveDeniedStatusTag(translate);
  if (item.resolution === TimeReportResolution.WITHDRAWN) {
    return resolveWithdrawnStatusTag(translate);
  }
  if (item.resolution === TimeReportResolution.OVERDUE) {
    return resolveOverdueStatusTag(translate);
  }
  if (isUnreportedItem(item)) return resolveUnreportedStatusTag(translate);
  if (item.status === TimeReportStatusV2.SUBMITTED) return resolveToApproveStatusTag(translate);
  return resolveReportedStatusTag(translate);
};

const resolveEditedStatusTag = (translate: TFunction): PersonDetailTag => {
  return { text: translate("TimeReports.Edited"), iconName: "v2/pen", tone: "warning" };
};

const resolveAbsenceStatusTag = (
  item: TimeReportsV2Item,
  translate: TFunction,
): PersonDetailTag => {
  return {
    text: getTimeReportAbsenceResolutionLabel(item.resolution, translate),
    iconName: "v2/cross-person",
    tone: "purple",
  };
};

const resolveDeniedStatusTag = (translate: TFunction): PersonDetailTag => {
  return {
    text: translate("TimeReports.Denied"),
    iconName: "v2/warning-triangle",
    tone: "danger",
  };
};

const resolveWithdrawnStatusTag = (translate: TFunction): PersonDetailTag => {
  return {
    text: translate("TimeReports.Withdrawn"),
    iconName: "v2/warning-triangle",
    tone: "danger",
  };
};

const resolveUnreportedStatusTag = (translate: TFunction): PersonDetailTag => {
  return {
    text: translate("TimeReports.StatusChipUnreported"),
    iconName: "v2/document",
    tone: "warning",
  };
};

const resolveOverdueStatusTag = (translate: TFunction): PersonDetailTag => {
  return {
    text: translate("CompanyTimeReport.Overdue"),
    iconName: "v2/warning-triangle",
    tone: "danger",
  };
};

const resolveToApproveStatusTag = (translate: TFunction): PersonDetailTag => {
  return { text: translate("TimeReports.ToApprove"), iconName: "v2/clock", tone: "info" };
};

const resolveApprovedStatusTag = (translate: TFunction): PersonDetailTag => {
  return {
    text: translate("TimeReports.Approved"),
    iconName: "v2/checkmark",
    tone: "success",
  };
};

const resolveReportedStatusTag = (translate: TFunction): PersonDetailTag => {
  return {
    text: translate("TimeReports.StatusChipReported"),
    iconName: "v2/document",
    tone: "info",
  };
};

const isApprovedStatus = (item: TimeReportsV2Item): boolean => {
  return (
    item.status === TimeReportStatusV2.APPROVED ||
    (item.status === TimeReportStatusV2.CLOSED &&
      (item.resolution === TimeReportResolution.TIME_REPORTED ||
        item.resolution === TimeReportResolution.PAYONDEMAND))
  );
};

export const isZeroLikeValue = (value: string): boolean => {
  const trimmedValue = value.trim().toLowerCase();
  return trimmedValue === "0h" || trimmedValue === "0m" || trimmedValue === "0";
};

export const createPersonSummaryViewModel = (
  row: TimeReportsV2Group,
): PersonRowSummaryViewModel => {
  const details = row.details;

  return {
    shifts: details?.timeReportCount ?? row.items.length,
    plannedTime: formatDetailsDuration(details?.planned ?? null, false),
    approvedTime: formatDetailsDuration(details?.approved ?? null, false),
    toApproveTime: formatDetailsDuration(details?.pending ?? null, false),
    unreportedTime: formatDetailsDuration(details?.open ?? null, false),
    absenceTime: formatAbsenceDuration(details?.absence ?? null),
    deviationTime: formatDetailsDuration(details?.deviation ?? null, true),
  };
};

const resolveWorkedMinutesData = (item: TimeReportsV2Item): WorkedMinutesData => {
  const plannedMinutes = getPlannedMinutes(item) ?? 0;
  const reportedMinutes = getReportedMinutes(item) ?? 0;
  const workedMinutes = reportedMinutes;
  const totalMinutes = workedMinutes > 0 ? workedMinutes : plannedMinutes;

  return {
    workedMinutes,
    plannedMinutes,
    totalMinutes,
  };
};

const resolveTotalTimeLabel = (
  item: TimeReportsV2Item,
  minutesData: WorkedMinutesData,
): string => {
  const signedMinutes = resolveSignedMinutesForResolution(
    minutesData.totalMinutes,
    item.resolution,
  );
  if (signedMinutes < 0) {
    return formatSignedMinutesDuration(signedMinutes);
  }
  return formatMinutesDuration(signedMinutes);
};

export const createPersonDetailRowViewModel = (
  item: TimeReportsV2Item,
  locale: string,
  deviationToleranceMinutes: number,
  includeFlexTimeDeviations: boolean,
  translate: TFunction,
): PersonDetailRowViewModel => {
  const workedMinutesData = resolveWorkedMinutesData(item);
  const dateSource = item.reportedStartTime ?? item.updatedAt;
  const deviation = resolveDeviationTagData(
    item,
    deviationToleranceMinutes,
    includeFlexTimeDeviations,
  );
  const isEdited = hasReportedVsStampedDifferenceOverThreshold(
    item,
    deviationToleranceMinutes,
  );
  const statusTag = resolveStatusTag(item, isEdited, translate);
  const deviationTag = resolveDeviationTag(deviation, translate);

  return {
    id: item.id,
    dateLabel: formatDayMonthLabel(dateSource, locale),
    weekdayLabel: formatWeekdayLabel(dateSource, locale),
    totalTimeLabel: resolveTotalTimeLabel(item, workedMinutesData),
    timeRows: resolveTimeRows(item, locale, translate),
    statusTag: statusTag,
    deviationTag: deviationTag,
  };
};

export const createPersonDetailSelection = (
  detail: PersonDetailRowViewModel,
  item: TimeReportsV2Item,
  row: TimeReportsV2Group,
): PersonDetailSelection => {
  return {
    detail,
    item,
    workerName: row.name,
    workerRole: row.role,
    workerExternalId: row.externalId,
    workGroupName: item.workGroupName ?? null,
  };
};

export const createUpdatedAtLabel = (
  updatedAt: string,
  locale: string,
  translate: TFunction,
): string => {
  const dateTime = formatDateTimeShortLabel(updatedAt, locale);
  if (dateTime === "--") return translate("TimeReports.ReportUpdated");
  return `${translate("TimeReports.ReportUpdated")} ${dateTime}`;
};
